import type { Motivation } from '@hmawiw/shared';
import { create } from 'zustand';
import { currentLocale, translate } from '../i18n';
import { useCards } from '../store/cards';
import { useUi } from '../store/ui';

export type TourStep = 'columns' | 'bands' | 'energy' | 'battery' | 'add' | 'finish';
export const TOUR_STEPS: TourStep[] = ['columns', 'bands', 'energy', 'battery', 'add', 'finish'];

const CARDS_KEY = 'hmawiw-tour-cards';

/** Three cards that give the tour something to point at; the first one lives in the Dreading column. */
const EXAMPLES: { key: 1 | 2 | 3; motivation: Motivation; band: 0 | 1 | 2; energy: 1 | 2 | 3; step?: true }[] = [
  { key: 1, motivation: 0, band: 0, energy: 2, step: true },
  { key: 2, motivation: 2, band: 1, energy: 3 },
  { key: 3, motivation: 1, band: 2, energy: 1 },
];

function readIds(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CARDS_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeIds(ids: string[]): void {
  try {
    if (ids.length) localStorage.setItem(CARDS_KEY, JSON.stringify(ids));
    else localStorage.removeItem(CARDS_KEY);
  } catch {
    // storage unavailable: the examples stay as ordinary cards
  }
}

export interface TourState {
  active: boolean;
  index: number;
  exampleIds: string[];
  start(): Promise<void>;
  next(): void;
  back(): void;
  /** Ends the tour; the example cards go unless the user chose to keep them. */
  finish(keepExamples: boolean): Promise<void>;
}

/** Example cards never reached the server, so they vanish without a tombstone. */
async function removeExamples(ids: string[]): Promise<void> {
  if (ids.length) await useCards.getState().discardLocal(ids);
  writeIds([]);
}

export const useTour = create<TourState>((set, get) => ({
  active: false,
  index: 0,
  exampleIds: readIds(),

  async start() {
    // Leftovers from an interrupted tour go first, so the board never fills up with examples.
    await removeExamples(get().exampleIds);
    const locale = currentLocale();
    const ids: string[] = [];
    for (const e of EXAMPLES) {
      const card = await useCards.getState().createCard({
        title: translate(locale, `tour.example.${e.key}`),
        notes: translate(locale, 'tour.exampleNote'),
        // The dreaded example shows the way in: a first step instead of a notes line.
        firstStep: e.step ? translate(locale, 'tour.exampleStep') : null,
        motivation: e.motivation,
        band: e.band,
        energy: e.energy,
        local: true,
      });
      ids.push(card.id);
    }
    writeIds(ids);
    useUi.getState().closeSheet();
    set({ active: true, index: 0, exampleIds: ids });
  },

  next() {
    set((s) => ({ index: Math.min(TOUR_STEPS.length - 1, s.index + 1) }));
  },

  back() {
    set((s) => ({ index: Math.max(0, s.index - 1) }));
  },

  async finish(keepExamples) {
    if (!keepExamples) await removeExamples(get().exampleIds);
    else {
      await useCards.getState().keepLocal(get().exampleIds);
      writeIds([]);
    }
    useUi.getState().dismissIntro();
    set({ active: false, index: 0, exampleIds: [] });
  },
}));
